import { useLocation } from 'react-router';
import { useEffect } from 'react';
import type { FC } from 'react';

import { useGlobalEvent } from '@luna/events';

import { ensureLeadingHashAndSlash } from './hash/hashUtils';

/**
 * LocationListener is a component that synchronizes the application's routing state
 * with our global event system.
 *
 * This component should be placed high in the component tree, typically near the root
 * of your application, to ensure it can listen to all route changes.
 *
 * @returns null - This component doesn't render anything visible
 *
 * @example
 * ```tsx
 * import { LocationListener } from './LocationListener';
 *
 * const App = () => (
 *   <Router>
 *     <LocationListener />
 *     <AppContent/>
 *   </Router>
 * );
 * ```
 *
 * @remarks
 * This component is essential for:
 * - Enabling parts of your application outside of React to react to route changes
 * - Triggering side effects that should occur on every route change
 *
 * Ensure this component is rendered only once in your application to avoid
 * duplicate event triggers.
 */
export const LocationListener: FC = () => {
    const location = useLocation();
    const trigger = useGlobalEvent('route:change');

    useEffect(() => {
        trigger(ensureLeadingHashAndSlash(location.pathname as string));
    }, [trigger, location.pathname]);

    return null;
};
